import { useCreateAssessmentMutation } from '@/app/redux/endpoints/assessments';
import {
  useBulkCreateStudentsMutation,
  useUpdateStudentMutation,
} from '@/app/redux/endpoints/students';
import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { ASSESSMENT_VERSION } from '@/common/types/migrations/assessment';
import { ControllerType, Student } from '@/common/types/student';
import ConfirmBulkAddModal from '@/common/ui/modals/ConfirmBulkAddModal';
import CsvImportModal, { CsvRow } from '@/common/ui/modals/CsvImportModal';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import generateLoginCards from '@/common/utils/generateLoginCards';
import { getSeatLimitStatus } from '@/common/utils/license';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  generateStudentPassword,
  initialGameSettings,
  initialStudent,
  isValidUsername,
  sanitizeUsername,
} from '@/common/utils/student';
import { showLearnerActionsTip } from '@/common/utils/tips';
import api from '@/db/apiClient';
import {
  CheckCircleIcon,
  CopySimpleIcon,
  EyeIcon,
  EyeSlashIcon,
  FileCsvIcon,
  PlusCircleIcon,
  ShuffleIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Alert } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import { RadioGroup, RadioGroupItem } from '@shadcn/components/radio-group';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { cn } from '@shadcn/lib/utils';
import { chunk } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import styled from 'styled-components';
import {
  addLearner,
  clearLearners,
  Learner,
  removeLearner,
  resetLearnerState,
  selectBulkAdd,
  updateLearner,
} from '../app/redux/slices/bulkAddSlice';

function BulkAdd() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { organisation } = useOrganisation();
  const organisationId = organisation?.id;
  const organisationName = organisation?.name;
  const licensesRemaining = organisation
    ? organisation.license.seatCount - organisation.studentCount
    : 0;
  const orgDefaultSettings =
    organisation?.defaultStudentSettings ?? initialGameSettings;

  const [isLoading, setLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [usernameStatuses, setUsernameStatuses] = useState<
    (boolean | undefined)[]
  >([]);
  const [doScreener, setDoScreener] = useState(false);
  const [scrollToSubmit, setScrollToSubmit] = useState(false);
  const addLearnersButtonRef = useRef<HTMLButtonElement>(null);

  const [updateStudent] = useUpdateStudentMutation();
  const [bulkCreateStudents] = useBulkCreateStudentsMutation();
  const [createAssessment] = useCreateAssessmentMutation();

  const userId = useAppSelector(selectUserId);
  const { learners } = useAppSelector(selectBulkAdd);

  const seatLimitReached = useMemo(() => {
    const status = getSeatLimitStatus(
      organisation?.license,
      organisation?.studentCount,
    );
    return status === 'at-limit' || status === 'over-limit';
  }, [organisation]);

  const allPasswordsVisible = useMemo(
    () => learners.length > 0 && learners.every((l) => l.passwordVisible),
    [learners],
  );

  const toggleAllPasswordsVisible = () => {
    learners.forEach((_, index) =>
      dispatch(
        updateLearner({
          index,
          field: 'passwordVisible',
          value: !allPasswordsVisible,
        }),
      ),
    );
  };

  const hasLearnersToAdd = useMemo(() => {
    if (!learners.length) return false;
    if (learners.length === 1) {
      const learner = learners[0];
      return learner.firstName && learner.surname && learner.username;
    }
    return true;
  }, [learners]);

  useEffect(() => {
    if (!dispatch) return;
    dispatch(resetLearnerState());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      const updatedUsernameStatuses: (boolean | undefined)[] = [];
      const uniqueUsernames = new Set<string>();

      for (const [index, learner] of learners.entries()) {
        if (!learner.username) {
          updatedUsernameStatuses[index] = undefined;
          continue;
        }
        if (!isValidUsername(learner.username)) {
          updatedUsernameStatuses[index] = false;
          continue;
        }
        const isAvailable =
          !!(await api.asEducator.isUsernameAvailable(learner.username)) &&
          !uniqueUsernames.has(learner.username);
        updatedUsernameStatuses[index] = isAvailable;
        uniqueUsernames.add(learner.username);
      }
      setUsernameStatuses(updatedUsernameStatuses);
    }, 500);

    return () => clearTimeout(timer);
  }, [learners]);

  useEffect(() => {
    if (scrollToSubmit && !isLoading) {
      addLearnersButtonRef.current?.scrollIntoView({ behavior: 'smooth' });
      setScrollToSubmit(false);
    }
  }, [isLoading, scrollToSubmit]);

  const handleAddLearner = () => {
    dispatch(
      addLearner({
        firstName: '',
        surname: '',
        username: '',
        password: generateStudentPassword(),
        passwordVisible: false,
      }),
    );
  };

  const handleRemoveLearner = (index: number) => {
    if (learners.length === 1) {
      dispatch(clearLearners());
      handleAddLearner();
      setUsernameStatuses([]);
      return;
    }
    dispatch(removeLearner(index));
    setUsernameStatuses((prevStatuses) => {
      const newStatuses = [...prevStatuses];
      newStatuses.splice(index, 1);
      return newStatuses;
    });
  };

  const handleOpenConfirmModal = () => {
    setConfirmModalOpen(true);
  };

  const handleOpenCsvModal = () => {
    setCsvModalOpen(true);
  };

  const handleConfirmCsvUpload = async (rows: CsvRow[]) => {
    setCsvModalOpen(false);
    dispatch(clearLearners());

    const validRows = rows.filter(
      (r) => r.firstName.trim() && r.lastName.trim(),
    );
    const rowsNeedingUsername = validRows.filter((r) => !r.username?.trim());

    const CHUNK = 50;
    const generatedUsernames: string[] = [];

    const needsUsernames = rowsNeedingUsername.length > 0;
    const toastId = needsUsernames
      ? toast.loading('Generating usernames...')
      : undefined;
    if (needsUsernames) setLoading(true);

    try {
      for (let i = 0; i < rowsNeedingUsername.length; i += CHUNK) {
        const chunkRows = rowsNeedingUsername.slice(i, i + CHUNK);
        const { usernames } = await api.asEducator.generateStudentUsernames({
          names: chunkRows.map((r) => ({
            firstName: r.firstName.trim(),
            lastName: r.lastName.trim(),
          })),
        });
        generatedUsernames.push(...usernames);
        if (toastId) {
          toast.loading(
            `Generating usernames (${generatedUsernames.length}/${rowsNeedingUsername.length})`,
            { id: toastId },
          );
        }
      }

      let genIdx = 0;
      const newLearners: Learner[] = validRows.map((row) => {
        const { firstName, lastName, username, password } = row;
        const resolvedUsername =
          (username && sanitizeUsername(username)) ||
          generatedUsernames[genIdx++];
        return {
          firstName: firstName.trim(),
          surname: lastName.trim(),
          username: resolvedUsername,
          password: password?.trim() || generateStudentPassword(),
          passwordVisible: false,
        };
      });

      newLearners.forEach((learner) => dispatch(addLearner(learner)));
      if (toastId) toast.dismiss(toastId);
    } catch (error) {
      console.error('Failed to import CSV', error);
      toast.error('Failed to import CSV', {
        id: toastId,
        description: UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      if (needsUsernames) setLoading(false);
      setScrollToSubmit(true);
    }
  };

  const handleConfirmBulkAdd = async (exportLoginCards: boolean) => {
    if (!userId || !organisationId) {
      console.error('Missing user ID or org ID');
      toast.error('Failed to add learners', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    setConfirmModalOpen(false);

    // Prepare all student objects
    const newStudents: Omit<Student, 'id'>[] = learners.map((learner) => ({
      ...initialStudent,
      studentFirstName: learner.firstName.trim(),
      studentSurname: learner.surname.trim(),
      spellcastUsername: learner.username,
      password: learner.password,
      createdBy: userId,
      organisationId,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      settings: orgDefaultSettings,
    }));

    // Validate all students before proceeding
    const invalidStudents = newStudents.filter(
      (s) => !s.studentFirstName || !s.studentSurname || !s.spellcastUsername,
    );

    if (invalidStudents.length > 0) {
      toast.error('Failed to add learners', {
        description: 'Some learners are missing required fields',
      });
      return;
    }

    setLoading(true);

    const toastId = toast.loading('Adding learners...', {
      description: "Please don't refresh or leave the page",
    });

    const chunks = chunk(newStudents, 50);
    const createdIds: string[] = [];
    const createdStudents: Student[] = [];

    try {
      for (let i = 0; i < chunks.length; i++) {
        const studentsChunk = chunks[i];
        const studentIds = await bulkCreateStudents({
          students: studentsChunk,
          organisationId,
        }).unwrap();
        createdIds.push(...studentIds);

        const chunkStudents = studentIds.map(
          (id, idx) => ({ id, ...newStudents[i * 50 + idx] }) as Student,
        );
        createdStudents.push(...chunkStudents);

        toast.loading(
          `Adding learners (${createdIds.length}/${newStudents.length})`,
          { id: toastId },
        );
      }

      trackMixpanelEvent('Students Bulk Created', {
        count: createdIds.length,
        organisationId,
        organisationName,
      });

      if (doScreener) {
        await handleCreateAssessments(createdIds);
      }

      if (exportLoginCards) {
        trackMixpanelEvent('Login cards generated during bulk add');
        generateLoginCards(createdStudents);
      }

      dispatch(clearLearners());
      handleAddLearner();

      navigate('/dashboard/learners');

      toast.success('Learners added', { id: toastId, description: '' });
      setTimeout(() => showLearnerActionsTip(), 500);
    } catch (error) {
      console.error('Error in bulk add:', error);
      if (createdStudents.length > 0) {
        toast.error('Failed to add learners', {
          id: toastId,
          description: `Added ${createdStudents.length} of ${newStudents.length}. Please retry.`,
        });
      } else {
        toast.error('Failed to add learners', {
          id: toastId,
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  async function handleCreateAssessments(studentIds: string[]) {
    if (!userId || !organisationId) {
      console.error('Missing user ID or org ID');
      toast.error('Failed to create assessments', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    const now = Date.now();
    const newAssessmentIds: string[] = [];

    for (const studentId of studentIds) {
      const newAssessment = await createAssessment({
        version: ASSESSMENT_VERSION,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        organisationId,
        phase: 0,
        placeAtAssessedLevel: true,
        control: ControllerType.CLICK,
        doScreener: true,
        students: {
          [studentId]: {
            startedAt: null,
            completedAt: null,
            placementLevel: null,
            phase: 0,
            placeAtAssessedLevel: true,
            control: ControllerType.CLICK,
            doScreener,
          },
        },
        studentIds: [studentId],
      }).unwrap();
      await updateStudent({
        orgId: organisationId,
        studentId,
        updates: { assessmentId: newAssessment.id },
      });
    }
    return newAssessmentIds;
  }

  const handleGenerateRandomUsername = async (index: number) => {
    const learner = learners[index];
    if (learner.firstName.trim() && learner.surname.trim()) {
      const { usernames } = await api.asEducator.generateStudentUsernames({
        names: [
          {
            firstName: learner.firstName.trim(),
            lastName: learner.surname.trim(),
          },
        ],
      });
      dispatch(
        updateLearner({ index, field: 'username', value: usernames[0] }),
      );
    }
  };

  const handleChange =
    (index: number, field: keyof Learner) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.trimStart();
      const processedValue =
        field === 'username' ? sanitizeUsername(value) : value;
      dispatch(updateLearner({ index, field, value: processedValue }));
    };

  const copyPasswordToAll = (index: number) => {
    const pw = learners[index].password;
    for (let i = 0; i < learners.length; i++) {
      dispatch(updateLearner({ index: i, field: 'password', value: pw }));
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <BulkAddHeader>
          <div className="flex flex-col">
            <BulkAddTitle className="font-semibold tracking-tight">
              Bulk Add Learners
            </BulkAddTitle>
            <p className="text-muted-foreground text-sm">
              Add multiple learners at once by filling out their details below
              or importing from a spreadsheet.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleOpenCsvModal}
            className="flex items-center gap-2"
            disabled={isLoading}
          >
            <FileCsvIcon className="h-5 w-5" />
            Import CSV
          </Button>
        </BulkAddHeader>

        <div className="p-6 space-y-6">
          {/* Seat Limit Warning */}
          {seatLimitReached && (
            <Alert variant="warning">
              <WarningCircleIcon size={18} weight="bold" className="ml-1" />
              <div>
                You have reached the maximum number of learner accounts for your
                plan. Go to your{' '}
                <a href="/dashboard/settings/billing" className="underline">
                  billing & subscription
                </a>{' '}
                page to add more learners.
              </div>
            </Alert>
          )}

          {/* Learner Input Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Learner Details</h2>

            {/* Column Headers */}
            <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 text-xs font-medium text-muted-foreground">
              <div className="pl-1">First Name</div>
              <div className="pl-1">Last Name</div>
              <div className="pl-1">Username</div>
              <div className="flex items-center gap-1 pl-1">
                <span>Password</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6"
                      onClick={toggleAllPasswordsVisible}
                    >
                      {allPasswordsVisible ? (
                        <EyeSlashIcon className="size-4" />
                      ) : (
                        <EyeIcon className="size-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {allPasswordsVisible ? 'Hide' : 'Show'} all passwords
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="w-[68px]"></div>
            </div>

            {/* Learner Rows */}
            <div className="space-y-2">
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-center"
                    >
                      <Skeleton className="h-8" />
                      <Skeleton className="h-8" />
                      <Skeleton className="h-8" />
                      <Skeleton className="h-8" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  ))
                : learners.map((learner: Learner, index: number) => (
                    <div
                      key={index}
                      className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-center"
                    >
                      <Input
                        placeholder="First name"
                        value={learner.firstName}
                        onChange={handleChange(index, 'firstName')}
                        disabled={isLoading}
                        className="h-9"
                      />

                      <Input
                        placeholder="Last name"
                        value={learner.surname}
                        onChange={handleChange(index, 'surname')}
                        disabled={isLoading}
                        className="h-9"
                      />

                      <div className="relative">
                        <Input
                          placeholder="Username"
                          value={learner.username}
                          onChange={handleChange(index, 'username')}
                          disabled={isLoading}
                          className="h-9"
                        />
                        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-white">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() =>
                                  handleGenerateRandomUsername(index)
                                }
                                disabled={
                                  isLoading ||
                                  !learner.firstName.trim() ||
                                  !learner.surname.trim()
                                }
                              >
                                <ShuffleIcon className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Generate username</TooltipContent>
                          </Tooltip>
                          {usernameStatuses[index] !== undefined && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center">
                                  {usernameStatuses[index] ? (
                                    <CheckCircleIcon
                                      size={16}
                                      className="text-green-500 mr-1"
                                    />
                                  ) : (
                                    <WarningCircleIcon
                                      size={16}
                                      className="text-red-500 mr-1"
                                    />
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                {usernameStatuses[index]
                                  ? 'Available'
                                  : 'Invalid or taken'}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      <div className="relative">
                        <Input
                          className="h-9"
                          type={learner.passwordVisible ? 'text' : 'password'}
                          value={learner.password}
                          onChange={handleChange(index, 'password')}
                          disabled={isLoading}
                        />

                        <div className="flex items-center gap-1 absolute right-1 top-1/2 -translate-y-1/2 bg-white">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() =>
                                  dispatch(
                                    updateLearner({
                                      index,
                                      field: 'password',
                                      value: generateStudentPassword(),
                                    }),
                                  )
                                }
                              >
                                <ShuffleIcon className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Generate random password
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() =>
                                  dispatch(
                                    updateLearner({
                                      index,
                                      field: 'passwordVisible',
                                      value: !learner.passwordVisible,
                                    }),
                                  )
                                }
                              >
                                {learner.passwordVisible ? (
                                  <EyeSlashIcon className="size-4" />
                                ) : (
                                  <EyeIcon className="size-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {learner.passwordVisible ? 'Hide' : 'Show'}{' '}
                              Password
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => copyPasswordToAll(index)}
                              disabled={
                                (learners.length === 1 &&
                                  Object.entries(learners[0]).every(
                                    ([key, value]) =>
                                      key === 'password' ||
                                      key === 'passwordVisible' ||
                                      value === '',
                                  )) ||
                                isLoading
                              }
                            >
                              <CopySimpleIcon className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Copy password to all</TooltipContent>
                        </Tooltip>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveLearner(index)}
                          disabled={
                            (learners.length === 1 &&
                              Object.entries(learners[0]).every(
                                ([key, value]) =>
                                  key === 'password' ||
                                  key === 'passwordVisible' ||
                                  value === '',
                              )) ||
                            isLoading
                          }
                        >
                          <XIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
            </div>

            <Button
              variant="outline"
              onClick={handleAddLearner}
              className="flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              <PlusCircleIcon className="h-4 w-4" weight="light" />
              Add Another Learner
            </Button>
          </div>

          {/* Placement Level Section */}
          <div className="pt-6 border-t space-y-4">
            <div>
              <h2 className="text-lg font-medium mb-1">
                Initial Placement Level
              </h2>
              <p className="text-sm text-muted-foreground">
                Choose how to set the starting level for these learners
              </p>
            </div>

            <RadioGroup
              defaultValue="noScreener"
              onValueChange={(value) => setDoScreener(value === 'doScreener')}
              className="grid grid-cols-2 gap-4"
            >
              <label
                htmlFor="r1"
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-all',
                  doScreener
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'hover:border-primary/50 hover:bg-accent/50',
                )}
              >
                <RadioGroupItem value="doScreener" id="r1" className="mt-0.5" />
                <div className="flex-1 space-y-1">
                  <div className="font-medium text-sm">
                    Let us find their level
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Learner will complete an assessment to automatically be
                    placed at the appropriate level.
                  </p>
                </div>
              </label>

              <label
                htmlFor="r2"
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-all',
                  !doScreener
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'hover:border-primary/50 hover:bg-accent/50',
                )}
              >
                <RadioGroupItem value="noScreener" id="r2" className="mt-0.5" />
                <div className="flex-1 space-y-1">
                  <div className="font-medium text-sm">Set level manually</div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    You can set the learner's level in the dashboard or create
                    an assessment later.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t">
          <Button
            ref={addLearnersButtonRef}
            disabled={usernameStatuses.includes(false) || !hasLearnersToAdd}
            loading={isLoading}
            onClick={handleOpenConfirmModal}
          >
            Add {learners.length}{' '}
            {learners.length === 1 ? 'Learner' : 'Learners'}
          </Button>
        </div>
      </div>

      <ConfirmBulkAddModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmBulkAdd}
        newLicenses={learners.length}
        licensesRemaining={licensesRemaining}
      />

      {csvModalOpen && (
        <CsvImportModal
          open={csvModalOpen}
          onClose={() => setCsvModalOpen(false)}
          onConfirm={handleConfirmCsvUpload}
        />
      )}
    </div>
  );
}

export default BulkAdd;

const BulkAddHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid hsl(var(--border));
`;

const BulkAddTitle = styled.span`
  font-size: 20px;
  font-weight: 500;
  color: hsl(var(--foreground));
`;
